import { convertFromHTML } from "draft-convert";
import { ContentState, convertFromRaw, convertToRaw } from "draft-js";
import { List } from "immutable";
import { withId } from "@utils/domain";
import { Workspace } from "@domain/group/workspace/workspace";
import { MediaDto, mediaFromDto, mediaToDto } from "@domain/media/dtos";
import { Media } from "@domain/media/media";
import { PersonDto, personFromDto, personToDto } from "@domain/user/dtos";
import { Course, COURSE_VERSION, DetailedCourse } from "./course";
import { Chapter, CourseContent } from "./courseContent";
import { ChapterDraft, CourseDraft } from "./courseDraft";
import { CourseSection, TextSection } from "./courseSection";
import { ExerciseDto, exerciseFromDto, exerciseToDto } from "@domain/lms/course/exercise/dtos";
import { ExerciseDraft } from "@domain/lms/course/exercise/exerciseDraft";

export interface CourseDraftDto extends Omit<
	CourseDraft,
	"description" | "content" | "banner" | "introduction" | "workspaceIds"
> {
	description: string;
	content: CourseContentDto;
	bannerId?: string;
	introductionId?: string;
	workspaceIds?: string[];
}

interface CourseContentDto extends Omit<CourseContent, "chapters" | "assets"> {
	chapters: ChapterDto[];
	assets?: MediaDto[];
}

interface ChapterDto extends Omit<Chapter, "sections" | "exercises" | "id"> {
	sections: CourseSectionDto[];
	exercises: ExerciseDto[];
}

type CourseSectionDto = Exclude<CourseSection, TextSection> | TextSectionDto;

interface TextSectionDto extends Omit<TextSection, "content"> {
	content: string;
}

export interface CourseDto extends Omit<
	Course,
	"author" | "publisher" | "description" | "lastUpdateDate" | "publishedAt" | "workspaceIds" | "banner" | "introduction"
> {
	author?: PersonDto;
	publisher?: PersonDto;
	description: string;
	lastUpdateDate: string;
	publishedAt?: string;
	banner?: MediaDto;
	introduction?: MediaDto;
	workspaces?: Workspace[];
}

export interface DetailedCourseDto extends Omit<CourseDto, "content"> {
	content: CourseContentDto;
}

export function courseDraftToDto(draft: CourseDraft): CourseDraftDto {
	const { content, banner, introduction } = draft;
	return {
		...draft,
		description: JSON.stringify(convertToRaw(draft.description)),
		bannerId: banner?.id,
		introductionId: introduction?.id,
		content: {
			...content,
			chapters: content.chapters.map((chapter) => chapterToDto(chapter)).toArray(),
			assets: undefined,
		},
		workspaceIds: draft.workspaceIds?.toArray(),
	};
}

function chapterToDto(chapter: ChapterDraft): ChapterDto {
	return {
		...chapter,
		sections: (chapter.sections.filter((section) => section.type !== "LOADING") as List<CourseSection>)
			.map((section) =>
				section.type === "TEXT" ? { ...section, content: JSON.stringify(convertToRaw(section.content)) } : section
			)
			.toArray(),
		exercises: chapter.exercises.toArray().map(exerciseToDto),
	};
}

export function courseToDto(course: Course): CourseDto {
	const { author, publisher, banner, introduction, description, lastUpdateDate, publishedAt } = course;
	return {
		...course,
		author: author ? personToDto(author) : undefined,
		publisher: publisher ? personToDto(publisher) : undefined,
		description: JSON.stringify(convertToRaw(description)),
		lastUpdateDate: lastUpdateDate.toISOString(),
		publishedAt: publishedAt ? publishedAt.toISOString() : undefined,
		banner: banner ? mediaToDto(banner) : undefined,
		introduction: introduction ? mediaToDto(introduction) : undefined,
	};
}

export function courseFromDto(course: CourseDto): Course {
	const { author, publisher, banner, introduction, description, lastUpdateDate, publishedAt, level } = course;
	return {
		...course,
		author: author ? personFromDto(author) : undefined,
		publisher: publisher ? personFromDto(publisher) : undefined,
		banner: banner ? mediaFromDto(banner) : undefined,
		introduction: introduction ? mediaFromDto(introduction) : undefined,
		description: contentStateFromString(description),
		lastUpdateDate: new Date(lastUpdateDate),
		publishedAt: publishedAt ? new Date(publishedAt) : undefined,
		level: level || undefined,
		contentType: "COURSE",
	};
}

export function detailedCourseFromDto(course: DetailedCourseDto): DetailedCourse {
	const {
		author,
		publisher,
		banner,
		introduction,
		description,
		lastUpdateDate,
		publishedAt,
		level,
		content: initialContent,
	} = course;
	let content: CourseContent;
	try {
		content = {
			...initialContent,
			chapters: List(initialContent.chapters.map(chapterFromDto)),
			assets: initialContent.assets ? List(initialContent.assets.map((asset) => mediaFromDto(asset))) : List(),
		};
	} catch {
		content = {
			version: COURSE_VERSION,
			chapters: List([
				withId({
					title: "PARSING ERROR",
					sections: List<CourseSection>(),
					exercises: List<ExerciseDraft>(),
				}),
			]),
			assets: List<Media>(),
		};
	}
	return {
		...course,
		author: author ? personFromDto(author) : undefined,
		publisher: publisher ? personFromDto(publisher) : undefined,
		banner: banner ? mediaFromDto(banner) : undefined,
		introduction: introduction ? mediaFromDto(introduction) : undefined,
		description: contentStateFromString(description),
		lastUpdateDate: new Date(lastUpdateDate),
		publishedAt: publishedAt ? new Date(publishedAt) : undefined,
		level: level || undefined,
		content,
		contentType: "COURSE",
	};
}

function chapterFromDto(dto: ChapterDto): Chapter {
	return withId({
		...dto,
		sections: List(dto.sections.map(sectionFromDto)),
		exercises: List(dto.exercises.map(exerciseFromDto)),
	});
}

function contentStateFromString(content: string): ContentState {
	try {
		return convertFromRaw(JSON.parse(content));
	} catch {
		return convertFromHTML({})(content);
	}
}

function sectionFromDto(section: CourseSectionDto): CourseSection {
	if (section.type === "TEXT") {
		return { ...section, content: contentStateFromString(section.content) };
	}
	return section;
}
